import React, { ReactNode, useEffect, useState } from 'react'

import { useLanguage } from 'language/translator'
import { useTheme } from '@mui/material'

import DriftingParticles from 'assets/animations/DriftingParticles'

import Box from 'components_new/atoms/Box'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'

import ChatAvatar from 'components_new/organisms/ChatBox/ChatAvatar'

import { DEFAULT_LOADING_DESCRIPTION } from './lib'

interface LoadingSectionProps {
  background?: boolean
  description?: string[]
  customSpinner?: ReactNode
  direction?: 'row' | 'column'
  disableLoadingDescription?: boolean
  title?: string
  scaleFactor?: number
  sx?: object
  variant?: 'default' | 'logo'
}

/**
 * The LoadingSection component is used
 * to show a loading indicator together
 * with a short descriptive text.
 *
 * It expands, and fills up, all
 * available space.
 */

const LoadingSection = React.forwardRef(
  (props: LoadingSectionProps, ref: any) => {
    const {
      background,
      description,
      customSpinner,
      direction = 'column',
      disableLoadingDescription,
      title,
      scaleFactor,
      sx,
      variant
    } = props

    const { lang } = useLanguage()
    const theme = useTheme()

    const [loadingDescription, setLoadingDescription] = useState(
      description ? description[0] : DEFAULT_LOADING_DESCRIPTION[lang][0]
    )
    const [current, setCurrent] = useState(0)

    useEffect(() => {
      const t = description ?? DEFAULT_LOADING_DESCRIPTION[lang]

      const descriptionSwapper = setInterval(() => {
        setLoadingDescription(t[current])
        if (current === t.length - 1) {
          setCurrent(0)
        } else {
          setCurrent(current + 1)
        }
      }, 3000)

      return () => {
        clearInterval(descriptionSwapper)
      }
    })

    const spinnerSize = scaleFactor
      ? scaleFactor * 40
      : direction === 'row'
        ? 24
        : 40

    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        aria-busy={true}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          display: 'flex',
          flexDirection: direction,
          fontSize: scaleFactor ? 16 * scaleFactor : undefined,
          gap: scaleFactor ? 2 * scaleFactor : 2,
          height: '100%',
          justifyContent: 'center',
          position: 'relative',
          ...sx
        }}
      >
        {background && 1 !== 1 ? (
          <Skeleton
            height={'100%'}
            width={'100%'}
            sx={{ left: 0, position: 'absolute', top: 0, zIndex: 0 }}
          />
        ) : null}

        {background ? (
          <DriftingParticles
            sx={{
              height: '100%',
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
              zIndex: 0
            }}
          />
        ) : null}

        <Box sx={{ zIndex: 1 }}>
          {customSpinner ? (
            customSpinner
          ) : variant === 'logo' ? (
            <Box
              sx={{
                height: spinnerSize,
                width: spinnerSize
              }}
            >
              <ChatAvatar active={true} />
            </Box>
          ) : (
            <LoadingSpinner
              color={background ? theme.palette.brand.main : undefined}
              size={spinnerSize}
            />
          )}
        </Box>

        {title ? (
          <Text
            align="center"
            color="text.primary"
            fontSize={scaleFactor ? 'inherit' : undefined}
            variant="h6"
          >
            {title}
          </Text>
        ) : null}

        {disableLoadingDescription ? null : (
          <Text
            align="center"
            color="text.disabled"
            variant="body2"
            fontWeight="medium"
            fontSize={scaleFactor ? 'inherit' : undefined}
            sx={{ zIndex: 1 }}
          >
            {loadingDescription}
            {loadingDescription ? '...' : null}
          </Text>
        )}
      </Box>
    )
  }
)

LoadingSection.displayName = 'LoadingSection'
export default LoadingSection
